import { EnterSearchInput } from "../../../components/ui/EnterSearchInput";
import { useTranslation } from "react-i18next";
import { Funnel, Search } from "lucide-react";
import { ui } from "../../../components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

/**
 * The filter bar every conveyance claim list wears — ESS, the manager queue
 * and the finance register alike, so the three read the same whichever login
 * you are on.
 *
 * One control, not a row of them. A line of five dropdowns costs the width of
 * the screen on a desktop and several stacked rows on a phone, all of it spent
 * before the first claim; here they live behind a single Filter button, with a
 * count of how many are actually narrowing the list so nothing is hidden.
 *
 * Search stays outside. It is not a filter you set and forget — it is typed,
 * read and cleared while looking at the rows, and burying it behind a click
 * would make the common case worse.
 *
 * Filters are described, not hard-coded: each screen passes what it has, and
 * this renders them in order. `value === allValue` is what "not filtering"
 * means, which is how the badge counts.
 */
export function ClaimFilterBar({
  search,
  onSearchChange,
  searchPlaceholder,
  filters = [],
  allValue = "all",
  onReset,
  className,
}) {
  const { t } = useTranslation();

  const activeCount = filters.filter((filter) => filter.value !== allValue).length;
  const canReset = Boolean(onReset) && (activeCount > 0 || Boolean(search));

  return (
    <div className={cn("mb-4 flex flex-wrap items-center gap-2.5", className)}>
      {onSearchChange ? (
        <div className="relative w-full sm:w-[260px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
          <EnterSearchInput data-page-search="true"
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            className={cn(ui.input, "h-9 w-full pl-9")}
          />
        </div>
      ) : null}

      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={ui.filterBtn}>
            <Funnel className="h-4 w-4" />
            {t("formActions.filter")}
            {activeCount > 0 ? (
              <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-white">
                {activeCount}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-72 flex flex-col gap-3">
          {filters.map((filter) => (
            <div key={filter.key}>
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wider per-file-hint">
                {filter.label}
              </p>
              <Select value={filter.value} onValueChange={filter.onChange}>
                <SelectTrigger className="h-10 w-full rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {filter.options.map((option) => (
                    <SelectItem key={String(option.value)} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}

          {canReset ? (
            <button
              type="button"
              onClick={onReset}
              className="h-9 w-full rounded-xl border border-(--color-text-border) text-[12px] font-bold per-file-hint hover:text-primary hover:border-primary/40 transition cursor-pointer"
            >
              {t("formActions.clearFilters")}
            </button>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default ClaimFilterBar;
